import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Gift, Trash2, Star, Pencil, Upload } from 'lucide-react';
import { queryKeys } from '@/lib/api/queryKeys';
import { stockApi } from './api';
import type { XkStock, StockClassification } from '@/lib/api/types';
import { usePlanQuota } from '@/lib/permissions/usePlanQuota';
import { UpgradeDialog } from '@/features/billing/UpgradeDialog';
import { isPlanLimitError } from '@/lib/permissions/quotaError';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { toast } from '@/components/ui/Toast';
import { Pagination } from '@/components/ui/Pagination';
import { ExportXlsxButton } from '@/components/ui/ExportXlsxButton';
import { usePagination } from '@/lib/hooks/usePagination';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { useAuth } from '@/lib/auth/useAuth';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import { useNavigate } from 'react-router-dom';
import { AddXotskiDialog, type NewXotski } from './AddXotskiDialog';
import { isOutline } from './libraryItems';
import { WarehousePanel, RecentMovements } from './WarehousePanel';
import { useCurrentBrand } from '@/lib/branding/useCurrentBrand';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { BulkImportDialog } from './import/BulkImportDialog';

// Classification UI hidden for now — every xotski gets the default
// classification under the hood (see AddXotskiDialog).

const RARITY_COLOR: Record<number, string> = {
  1: 'var(--color-rarity-1)',
  2: 'var(--color-rarity-2)',
  3: 'var(--color-rarity-3)',
  4: 'var(--color-rarity-4)',
  5: 'var(--color-rarity-5)',
};

export function StockListPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  useFavoritable(t('sidebar.myXotskies'));
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<XkStock | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Classification filter is hidden; keep the variable as a const ''
  // sentinel so the filtering branch below stays a no-op.
  const filter: StockClassification | '' | 'UNCLASSIFIED' = '';
  const quota = usePlanQuota('xotskiStocks');

  const stocks = useQuery({
    queryKey: queryKeys.stock.all,
    queryFn: () => stockApi.list(),
  });

  // CH-11 — el merch va a la marca ACTIVA del selector del espacio de
  // trabajo, no a la empresa del usuario: con varias marcas eran distintas y
  // el ítem aterrizaba en la raíz sin que nadie lo dijera.
  const selectedBrandId = useSelectedBrandId();
  const { brands, hasMultiple } = useCurrentBrand();
  const destinationId = selectedBrandId ?? user?.companyId ?? undefined;
  const destinationName = brands.find((b) => b.id === destinationId)?.name ?? '';
  const destination = destinationName ? { name: destinationName, confirm: hasMultiple } : undefined;

  const create = useMutation({
    mutationFn: (v: NewXotski) =>
      stockApi.create({ ...v, companyId: destinationId }),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.byBrand(destinationId ?? 0) });
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.current });
      setCreateOpen(false);
      toast({
        tone: 'success',
        title: `${t('stock.list.createdTitle')}: "${v.name}"`,
        description: destinationName ? t('stock.list.addedTo', { brand: destinationName }) : undefined,
        action: { label: t('stock.list.seeInventory'), onClick: () => navigate('/brand-inventory') },
      });
    },
    onError: (e) => {
      if (isPlanLimitError(e)) {
        setCreateOpen(false);
        setUpgradeOpen(true);
        return;
      }
      toast({
        tone: 'error',
        title: t('stock.list.createFailed'),
        description: (e as Error).message,
      });
    },
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: number; data: NewXotski }) => stockApi.update(id, data),
    onSuccess: (_, { data }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all });
      setEditing(null);
      toast({ tone: 'success', title: `${t('stock.list.updatedTitle')}: "${data.name}"` });
    },
    onError: (e) =>
      toast({
        tone: 'error',
        title: t('stock.list.updateFailed'),
        description: (e as Error).message,
      }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => stockApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.stock.all }),
  });

  const allItems = stocks.data?.items;
  // Classification filter UI is hidden; `filter` defaults to '' so the
  // full list shows. Per-bucket counts are no longer needed.
  const items =
    filter === ''
      ? allItems
      : filter === 'UNCLASSIFIED'
        ? allItems?.filter((s) => !s.classification)
        : allItems?.filter((s) => s.classification === filter);

  // 12 cards per page is the project-wide default. `usePagination` clamps
  // automatically when the filtered list shrinks.
  const pager = usePagination(items, 12);

  return (
    <div className="page space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            {t('stock.list.title')}
            <HelpHint section="stock-list" />
          </h1>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
            {t('stock.list.subtitle')}
          </p>
        </div>
        <span data-tour="stock-create">
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => (quota.reached ? setUpgradeOpen(true) : setCreateOpen(true))}
          >
            {t('stock.list.addButton')}
          </Button>
        </span>
      </header>

      {/* Maqueta «My Merch»: el almacén (qué hay, comprometido, reposición, sin
          destino), la acción prioritaria y los lotes sin destino con su
          disposición. Todo sale de los mismos hechos que el informe. */}
      <WarehousePanel
        brandId={destinationId ?? null}
        stocks={allItems ?? []}
        onEditStock={(stock) => setEditing(stock)}
      />


      <div className="flex flex-wrap items-center gap-3">
        {/* Classification filter hidden — kept the state above (`filter`)
            so the card grid keeps using the same code paths; can be
            re-exposed by restoring the <Select> block. */}
        {items && (
          <span className="text-xs uppercase tracking-wide text-[var(--color-fg-muted)]">
            {items.length} {items.length === 1 ? t('stock.list.itemOne') : t('stock.list.itemMany')}
          </span>
        )}
        <button
          type="button"
          data-tour="stock-import"
          onClick={() => setImportOpen(true)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-default)] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-[var(--color-fg-primary)] transition-colors hover:border-[var(--color-accent-ink)] hover:bg-[var(--color-bg-surface)]"
        >
          <Upload className="h-3.5 w-3.5" />
          {t('stock.list.importButton')}
        </button>
        <ExportXlsxButton
          filename="xotskies"
          sheetName="Xotskies"
          disabled={!items || items.length === 0}
          rows={() => (items ?? []).map(stockToExportRow)}
        />
      </div>

      {stocks.isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-72 rounded-xl" />
          ))}
        </div>
      ) : !items || items.length === 0 ? (
        filter === '' ? (
          <EmptyState
            icon={<Gift className="h-6 w-6" />}
            title={t('stock.list.emptyTitle')}
            description={t('stock.list.emptyBody')}
          />
        ) : (
          <EmptyState
            icon={<Gift className="h-6 w-6" />}
            title={
              filter === 'UNCLASSIFIED'
                ? t('stock.list.allClassified')
                : `${t('stock.list.noneInClass')} ${filter}`
            }
            description={
              filter === 'UNCLASSIFIED'
                ? t('stock.list.allClassifiedBody')
                : `${t('stock.list.editToSetClass')} ${filter} ${t('stock.list.editToSetClassSuffix')}`
            }
          />
        )
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {pager.pageItems.map((s) => (
              <XotskiCard
                key={s.id}
                stock={s}
                onEdit={() => setEditing(s)}
                onDelete={() => remove.mutate(s.id)}
              />
            ))}
          </div>
          <Pagination {...pager} noun="xotskies" />
        </>
      )}

      <RecentMovements />

      <AddXotskiDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSave={(v) => create.mutate(v)}
        loading={create.isPending}
        destination={destination}
      />

      <AddXotskiDialog
        open={editing !== null}
        onOpenChange={(v) => !v && setEditing(null)}
        destination={destination}
        initial={editing ? stockToInitial(editing) : null}
        onSave={(v) => {
          if (editing) update.mutate({ id: editing.id, data: v });
        }}
        loading={update.isPending}
      />

      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        resource="xotskiStocks"
        currentPlanName={quota.planName}
        limit={quota.limit}
      />

      <BulkImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        existingStock={allItems ?? []}
      />
    </div>
  );
}

// ── XotskiCard ─────────────────────────────────────────────────────────────

function XotskiCard({
  stock,
  onEdit,
  onDelete,
}: {
  stock: XkStock;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const t = useT();
  const r = Math.max(1, Math.min(5, stock.rating ?? 3));
  const rarity = {
    label: t(`stock.card.rarityLabel.${r}`),
    color: RARITY_COLOR[r],
  };
  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] transition-all hover:-translate-y-0.5 hover:border-[var(--color-accent-ink)]">
      {/* ── Image header ─────────────────────────────────────── */}
      <div className="relative h-40 overflow-hidden">
        {stock.image ? (
          <img
            src={resolveImageUrl(stock.image)}
            alt={stock.name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <ImageFallback name={stock.name} accent={rarity.color} />
        )}

        {/* CH-14 — el ítem sigue con una silueta de la biblioteca: falta la foto real. */}

        {isOutline(stock.image) && (

          <span

            className="absolute right-2 bottom-2 z-10 rounded-full border border-[var(--color-warn)] bg-[rgb(234_179_8/0.15)] px-1.5 text-[9px] font-extrabold uppercase tracking-wide text-[var(--color-fg-primary)]"

            title={t('stock.outline.placeholderHint')}

          >

            {t('stock.outline.badge')}

          </span>

        )}

        {/* Rarity badge — top-left */}
        <span
          className="absolute left-2.5 top-2.5 rounded px-2 py-0.5 text-[9px] font-black uppercase tracking-wider shadow-sm"
          style={{
            backgroundColor: rarity.color,
            // The ramp runs light → dark: navy holds 6.6:1 through tier 4 but
            // falls to 3.1:1 on the tier-5 olive, so tier 5 flips to light ink.
            // Literals like the ramp — --color-rarity-* live only on :root.
            color: r >= 5 ? '#f5f5f3' : '#1a1a2e',
            fontFamily: 'var(--font-display)',
          }}
          title={`${t('stock.card.rarityTier')} ${stock.rating}`}
        >
          {rarity.label}
        </span>

        {/* Logo — top-right (always shows; falls back to monogram) */}
        <div className="absolute right-2.5 top-2.5 grid h-9 w-9 place-items-center overflow-hidden rounded-md border border-white/20 bg-[rgb(0_0_0/0.5)] p-1 shadow-sm backdrop-blur-sm">
          {stock.logo ? (
            <img
              src={resolveImageUrl(stock.logo)}
              alt=""
              className="h-full w-full object-contain"
              aria-hidden="true"
            />
          ) : (
            <LogoFallback name={stock.name} />
          )}
        </div>

        {/* Edit + Delete (hover) — bottom-right */}
        <div className="absolute bottom-2.5 right-2.5 flex gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            onClick={onEdit}
            aria-label={`${t('stock.card.edit')} ${stock.name}`}
            className="rounded-md bg-[rgb(0_0_0/0.6)] p-1.5 text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent)] hover:text-[var(--color-fg-on-accent)]"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label={`${t('stock.card.delete')} ${stock.name}`}
            className="rounded-md bg-[rgb(0_0_0/0.6)] p-1.5 text-white transition-colors hover:bg-[var(--color-error)]"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div>
          <h3
            className="line-clamp-1 text-[15px] font-extrabold tracking-tight text-[var(--color-xk-lavender)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {stock.name}
          </h3>
          {stock.translate && (
            <p className="line-clamp-1 text-[11px] italic text-[var(--color-fg-subtle)]">
              {stock.translate}
            </p>
          )}
        </div>

        <p className="line-clamp-2 min-h-[2.5em] text-[12px] leading-snug text-[var(--color-fg-muted)]">
          {stock.description || '—'}
        </p>

        {/* Rating stars */}
        <Stars value={stock.rating ?? 0} />

        {/* Meta footer. El valor unitario sólo sale cuando existe: sin valor,
            la tarjeta se ve exactamente igual que antes (CH-01). */}
        <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-[var(--color-border-subtle)] pt-3">
          <MetaPill label={t('stock.card.qty')} value={String(stock.initialQuantity ?? 0)} />
          {stock.restockThreshold != null && stock.restockThreshold > 0 && (
            <MetaPill label="≤" value={String(stock.restockThreshold)} />
          )}
          {stock.unitCost != null && Number(stock.unitCost) > 0 && (
            <MetaPill label="$" value={Number(stock.unitCost).toFixed(2)} />
          )}
        </div>
      </div>
    </article>
  );
}

function Stars({ value }: { value: number }) {
  const t = useT();
  const clamped = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <div
      className="flex items-center gap-1"
      aria-label={`${t('stock.card.ratingOf')} ${clamped}/5`}
    >
      {[1, 2, 3, 4, 5].map((i) => {
        const on = i <= clamped;
        return (
          <Star
            key={i}
            className="h-3.5 w-3.5"
            strokeWidth={1.5}
            color={on ? '#F5C518' : 'rgb(255 255 255 / 0.2)'}
            fill={on ? '#F5C518' : 'transparent'}
          />
        );
      })}
      <span className="ml-1 text-[11px] font-bold tabular-nums text-[var(--color-fg-muted)]">
        {clamped}/5
      </span>
    </div>
  );
}

function MetaPill({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border-subtle)] bg-[rgb(255_255_255/0.03)] px-2 py-0.5 text-[10px] tabular-nums text-[var(--color-fg-muted)]">
      <span className="font-bold text-[var(--color-fg-subtle)]">{label}</span>
      <span className="font-extrabold text-[var(--color-xk-lavender)]">{value}</span>
    </span>
  );
}

// ── Fallbacks ──────────────────────────────────────────────────────────────

/** Initials derived from the name (max 2 chars). Used by both fallbacks. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '◇';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Big image fallback — gradient banner with Gift icon + monogram. */
function ImageFallback({ name, accent }: { name: string; accent: string }) {
  return (
    <div
      className="relative flex h-full w-full items-center justify-center overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${accent}26 0%, var(--color-xk-bg) 70%)`,
      }}
    >
      {/* Decorative gradient ring */}
      <div
        aria-hidden="true"
        className="absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-20 blur-2xl"
        style={{ background: accent }}
      />
      <div className="z-10 flex flex-col items-center gap-1.5 text-[var(--color-xk-text-muted)]">
        <Gift className="h-8 w-8" strokeWidth={1.5} />
        <span
          className="text-[13px] font-black tracking-widest text-[var(--color-xk-lavender)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {initials(name)}
        </span>
      </div>
    </div>
  );
}

/** Small logo fallback — monogram on dark tile. */
function LogoFallback({ name }: { name: string }) {
  return (
    <span
      className="text-[11px] font-black tracking-wide text-[var(--color-xk-lavender)]"
      style={{ fontFamily: 'var(--font-display)' }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

/** Flatten a stock row for XLSX export. Keep keys human-readable. */
function stockToExportRow(s: XkStock): Record<string, unknown> {
  return {
    ID: s.id,
    Name: s.name,
    Description: s.description ?? '',
    Translate: s.translate ?? '',
    Image: s.image ?? '',
    Logo: s.logo ?? '',
    Classification: s.classification ?? '',
    Rating: s.rating ?? '',
    'Initial qty': s.initialQuantity ?? '',
    'Restock threshold': s.restockThreshold ?? '',
    Status: s.status ?? '',
    'Created at': s.createdAt ?? '',
  };
}

/** Maps a persisted XkStock row → the dialog's editable shape. */
function stockToInitial(s: XkStock): NewXotski {
  return {
    name: s.name,
    description: s.description ?? undefined,
    translate: s.translate ?? undefined,
    image: s.image ?? undefined,
    logo: s.logo ?? undefined,
    unitCost: s.unitCost != null ? Number(s.unitCost) : undefined,
    initialQuantity: s.initialQuantity ?? undefined,
    classification: s.classification ?? undefined,
    rating: s.rating ?? 3,
    restockThreshold: s.restockThreshold ?? undefined,
  };
}
